/**
 * Service worker registration and update flow.
 *
 * Two deliberate choices:
 *
 * - **Never auto-reload.** A worker that reloads the page while someone is
 *   mid-stroke destroys unsaved work, so a new version waits until the user asks
 *   for it. The banner is the whole UX.
 * - **Silent when unsupported.** No service worker (http, older browsers, private
 *   mode) means the editor still works; it just cannot promise offline.
 */

export type UpdateState = 'none' | 'ready';

let registration: ServiceWorkerRegistration | null = null;

export async function registerServiceWorker(onUpdate: (state: UpdateState) => void): Promise<void> {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
  try {
    registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    // Registration failures are environmental (offline, blocked) and non-fatal.
    return;
  }

  const notify = () => {
    // Only offer a reload if a previous version was already in control; on a
    // first visit the installing worker is not an "update".
    onUpdate(navigator.serviceWorker.controller && registration?.waiting ? 'ready' : 'none');
  };

  registration.addEventListener('updatefound', () => {
    const worker = registration?.installing;
    if (!worker) return;
    worker.addEventListener('statechange', notify);
  });
  navigator.serviceWorker.addEventListener('controllerchange', notify);
  notify();
}

/** Hand control to the waiting worker, then reload once it is in charge. */
export function applyUpdate(): void {
  const worker = registration?.waiting;
  if (!worker) return;
  let reloaded = false;
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    },
    { once: true },
  );
  worker.postMessage('SKIP_WAITING');
}
